import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import { V4ThemeProvider, useV4ColorMode } from '../v4/theme';
import { RequireAuth, useShell } from '../v4/shell';
import GrowSetupPicker from '../v4/components/GrowSetupPicker';
import { ThemeMenu, VariantSwatch } from '../v4/components/ThemePicker';
import { V6_VARIANT } from './theme';

const MONO = '"Space Mono","Courier New",monospace';
const RULE = '2px solid var(--v4-text)';
const pad2 = (n) => String(n).padStart(2, '0');

/* Plain text button in the header — uppercase, underline on hover */
function HeaderAction({ children, onClick, accent = false }) {
  return (
    <Box component="button" onClick={onClick} sx={{
      all: 'unset', cursor: 'pointer',
      fontFamily: MONO, fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
      px: 1.5, height: '100%', display: 'flex', alignItems: 'center',
      borderLeft: RULE,
      color: accent ? 'var(--v4-bg)' : 'var(--v4-text)',
      background: accent ? 'var(--v4-cyan)' : 'transparent',
      '&:hover': { background: accent ? 'var(--v4-text)' : 'rgba(var(--v4-fg-rgb),0.06)' },
    }}>
      {children}
    </Box>
  );
}

/* Theme list styled like the tab strip: 01 STANDARD  v4 */
function renderThemeItem(v, { active, index, mode }) {
  return (
    <>
      <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: '0.75rem', color: 'var(--v4-cyan)', width: 26 }}>{pad2(index + 1)}</Typography>
      <VariantSwatch variant={v} mode={mode} size={16} round={false} />
      <Typography sx={{ flex: 1, ml: 1.25, fontFamily: MONO, fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', textDecoration: active ? 'underline' : 'none', textUnderlineOffset: 4 }}>
        {v.label}
      </Typography>
      <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', color: 'rgba(var(--v4-muted-rgb),0.8)', ml: 2 }}>/{v.id.slice(1).padStart(2, '0')}</Typography>
    </>
  );
}

function Header() {
  const shell = useShell();
  const { mode, toggleMode, variant } = useV4ColorMode();
  const [themeAnchor, setThemeAnchor] = useState(null);

  return (
    <Box component="header" sx={{ borderBottom: RULE, display: 'flex', alignItems: 'stretch', height: 64, background: 'var(--v4-bg)' }}>
      {/* Wordmark */}
      <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ px: { xs: 2, md: 3 }, alignSelf: 'center', flex: { xs: 1, md: 'none' } }}>
        <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.8rem' }, letterSpacing: '-0.04em', color: 'var(--v4-text)', lineHeight: 1 }}>
          ECOSCOPE
        </Typography>
        <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: '0.9rem', color: 'var(--v4-cyan)' }}>/06</Typography>
      </Stack>

      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', px: 2, borderLeft: RULE, flex: 1 }}>
        <GrowSetupPicker />
      </Box>

      {shell.unsavedCount > 0 && (
        <HeaderAction accent onClick={shell.saveConfig}>
          Speichern ({shell.unsavedCount})
        </HeaderAction>
      )}
      <HeaderAction onClick={e => setThemeAnchor(e.currentTarget)}>Theme</HeaderAction>
      <HeaderAction onClick={toggleMode}>{mode === 'dark' ? 'Hell' : 'Dunkel'}</HeaderAction>
      <ThemeMenu
        anchorEl={themeAnchor} onClose={() => setThemeAnchor(null)} current={variant.id}
        renderItem={renderThemeItem}
        paperSx={{ mt: 0, borderRadius: 0, border: RULE, boxShadow: 'none', background: 'var(--v4-bg)' }}
        itemSx={{ borderBottom: '1px solid rgba(var(--v4-fg-rgb),0.12)', '&:last-of-type': { borderBottom: 'none' }, '&.Mui-selected': { background: 'rgba(var(--v4-cyan-rgb),0.1)' } }}
      />
      <Box onClick={() => shell.go('/settings')} sx={{ display: 'flex', alignItems: 'center', px: 1.5, borderLeft: RULE, cursor: 'pointer' }}>
        <Avatar src={shell.avatar} sx={{ width: 30, height: 30, border: RULE }} />
      </Box>
      <HeaderAction onClick={shell.logout}>Logout</HeaderAction>
    </Box>
  );
}

/* Numbered tab strip: 01 DASHBOARD  02 ANALYTICS ... */
function Tabs() {
  const shell = useShell();
  return (
    <Box component="nav" sx={{
      display: 'flex', borderBottom: RULE, overflowX: 'auto', background: 'var(--v4-bg)',
      scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
    }}>
      {shell.items.map(({ label, path }, i) => {
        const active = shell.isActive(path);
        return (
          <Box key={path} onClick={() => shell.go(path)} sx={{
            flex: { md: 1 }, minWidth: { xs: 120, md: 0 },
            px: 2, py: 1.25, cursor: 'pointer',
            borderRight: i < shell.items.length - 1 ? RULE : 'none',
            background: active ? 'var(--v4-text)' : 'transparent',
            color: active ? 'var(--v4-bg)' : 'var(--v4-text)',
            transition: 'background .12s linear',
            '&:hover': { background: active ? 'var(--v4-text)' : 'rgba(var(--v4-cyan-rgb),0.12)' },
          }}>
            <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', color: active ? 'var(--v4-cyan)' : 'rgba(var(--v4-muted-rgb),0.8)' }}>
              {pad2(i + 1)}
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.02em', color: 'inherit', whiteSpace: 'nowrap' }}>
              {label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function TitleBlock() {
  const shell = useShell();
  const [now, setNow] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(id); }, []);
  const idx = shell.current ? shell.items.indexOf(shell.current) + 1 : 0;

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'flex-end' }} justifyContent="space-between"
      sx={{ borderBottom: RULE, pb: 2, mb: 4, gap: 1 }}>
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: '1rem', color: 'var(--v4-cyan)', mt: 1 }}>
          {pad2(idx)}
        </Typography>
        <Typography component="h1" sx={{
          fontWeight: 700, lineHeight: 0.9, letterSpacing: '-0.05em', textTransform: 'uppercase',
          fontSize: 'clamp(2.6rem, 7vw, 5.5rem)', color: 'var(--v4-text)',
        }}>
          {shell.current?.label ?? 'EcoScope'}
        </Typography>
      </Stack>
      <Box sx={{ textAlign: { sm: 'right' }, fontFamily: MONO }}>
        <Typography sx={{ fontFamily: MONO, fontSize: '1.4rem', fontWeight: 700, color: 'var(--v4-text)', lineHeight: 1 }}>
          {pad2(now.getHours())}:{pad2(now.getMinutes())}:{pad2(now.getSeconds())}
        </Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: '0.75rem', color: 'rgba(var(--v4-muted-rgb),0.85)', textTransform: 'uppercase' }}>
          {now.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}
        </Typography>
      </Box>
    </Stack>
  );
}

function Shell() {
  return (
    <Box sx={{
      minHeight: '100vh',
      /* faint 8px baseline grid */
      backgroundImage: 'linear-gradient(rgba(var(--v4-fg-rgb),0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(var(--v4-fg-rgb),0.04) 1px, transparent 1px)',
      backgroundSize: '48px 48px',
    }}>
      <Box sx={{ position: 'sticky', top: 0, zIndex: 100 }}>
        <Header />
        <Tabs />
      </Box>
      <Box sx={{ display: { xs: 'block', md: 'none' }, borderBottom: RULE, p: 1.5, background: 'var(--v4-bg)' }}>
        <GrowSetupPicker />
      </Box>
      <Box component="main" sx={{ px: { xs: 2, md: 4 }, py: { xs: 3, md: 4 }, maxWidth: 1600, mx: 'auto' }}>
        <TitleBlock />
        <Outlet />
      </Box>
    </Box>
  );
}

export default function V6Layout() {
  return (
    <V4ThemeProvider variant={V6_VARIANT}>
      <RequireAuth>
        <Shell />
      </RequireAuth>
    </V4ThemeProvider>
  );
}
